import { useCallback, useMemo } from 'react'

import { Badge, Box, Group, Stack, Text } from '@mantine/core'
import { IconMovie, IconPlayCardStar } from '@tabler/icons-react'
import { Link, useGetList, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import {
  PromotionOperationAttributesApiDTO,
  PromotionOperationStatusApiDTO,
  UserApiDTO,
} from '../../codegen/generated/Api.schemas'
import { useGetApiUsers } from '../../codegen/generated/users'
import { DateHelper } from '../../helpers/Date.helper'
import { VideoPromotionHelper } from '../../helpers/VideoPromotion.helper'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import { LXLoader } from '../LXLoader'
import { LXTable, LXTableColumn } from '../LXTable/LXTable'

type LXVideoOperationsTableType = PromotionOperationAttributesApiDTO & {
  date: string
  nameItem: JSX.Element
  youtubeChannel: JSX.Element
  statusItem: JSX.Element
  configuration: JSX.Element
  results: JSX.Element
  createdBy: JSX.Element
}

type VideosPromotionsOperationsListProps = {
  operations: PromotionOperationAttributesApiDTO[]
  isLoading: boolean
}

export const VideosPromotionsOperationsList = ({
  operations,
  isLoading,
}: VideosPromotionsOperationsListProps) => {
  const t = useTranslate()
  const navigate = useNavigate()

  const usersQuery = useGetApiUsers()
  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })

  const columns: LXTableColumn<LXVideoOperationsTableType>[] = useMemo(
    () => [
      { key: 'date', value: t('_generics.date'), size: 'md' },
      { key: 'nameItem', value: t('_generics.name'), size: 'md', type: 'component' },
      {
        key: 'youtubeChannel',
        value: t('_generics.youtubeChannel'),
        type: 'component',
        size: 'md',
      },
      { key: 'configuration', value: t('_generics.configuration'), size: 'md', type: 'component' },
      { key: 'videosCount', value: t('_generics.videos'), size: 'sm' },
      { key: 'results', value: t('_generics.results'), type: 'component' },
      {
        key: 'createdBy',
        value: t('pages.videoPromotion.createdBy'),
        size: 'sm',
        type: 'component',
      },
      {
        key: 'statusItem',
        value: t('_generics.status'),
        type: 'component',
        size: 'sm',
      },
    ],
    [t],
  )

  const getStatus = useCallback(
    (statusId: PromotionOperationStatusApiDTO) => {
      const status = VideoPromotionHelper.getStatus(statusId)

      return (
        <Badge color={status.color}>
          {t(`pages.videoPromotion.statuses.${status.id}`, { defaultValue: status.id })}
        </Badge>
      )
    },
    [t],
  )

  const getConfiguration = useCallback(
    (o: PromotionOperationAttributesApiDTO) => {
      const config = []

      if (o.config.endscreen) {
        config.push(
          <Badge
            key="endscreen"
            variant="default"
            radius="sm"
            leftSection={<IconMovie size={12} />}
          >
            <Box pt={3}>{t('pages.videoPromotion.endScreen._title')}</Box>
          </Badge>,
        )
      }
      if (o.config.infocards) {
        config.push(
          <Badge
            key="infocards"
            variant="default"
            radius="sm"
            leftSection={<IconPlayCardStar size={12} />}
          >
            <Box pt={3}>{t('pages.videoPromotion.infocards')}</Box>
          </Badge>,
        )
      }

      if (config.length > 0) {
        return <Stack>{config}</Stack>
      }

      return <></>
    },
    [t],
  )

  const getUserName = useCallback(
    (userId: UserApiDTO['id'] | undefined) => {
      const user = usersQuery.data?.body.find(u => u.id === userId)

      if (user) {
        return <Text size="sm">{user.email}</Text>
      }

      return (
        <Text size="sm" fs="italic" c="dimmed">
          {t('_generics.undefined')}
        </Text>
      )
    },
    [usersQuery.data, t],
  )

  const getResults = useCallback(
    (o: PromotionOperationAttributesApiDTO) => {
      const badges = [] as JSX.Element[]

      if (o.successCount > 0) {
        badges.push(
          <Badge key="success" color="green" variant="light">
            {t('pages.videoPromotion.statuses.success')} : {o.successCount}
          </Badge>,
        )
      }
      if (o.failureCount > 0) {
        badges.push(
          <Badge key="failed" color="red" variant="light">
            {t('pages.videoPromotion.statuses.failed')} : {o.failureCount}
          </Badge>,
        )
      }
      if (o.skippedCount > 0) {
        badges.push(
          <Badge key="skipped" color="orange" variant="light">
            {t('pages.videoPromotion.statuses.skipped')} : {o.skippedCount}
          </Badge>,
        )
      }

      if (badges.length > 0) {
        return <Stack gap="xs">{badges}</Stack>
      }

      return <></>
    },
    [t],
  )

  const items = useMemo(
    () =>
      (operations ?? []).map(o => ({
        ...o,
        date: DateHelper.format(o.createdAt, 'DD-MM-YYYY HH:mm'),
        nameItem: o.name ? (
          <Text size="sm" fw={600}>
            {o.name}
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
        youtubeChannel: (
          <Link to={`/youtubeChannels/${o.youtubeChannelId}/show`}>
            {youtubeChannelsQuery.data?.find(c => c.id === o.youtubeChannelId)?.name ??
              o.youtubeChannelId}
          </Link>
        ),
        configuration: getConfiguration(o),
        results: getResults(o),
        createdBy: getUserName(o.createdByUserId),
        statusItem: getStatus(o.status),
      })),
    [operations, getUserName, getConfiguration, getResults, getStatus, youtubeChannelsQuery.data],
  )

  if (isLoading) {
    return <LXLoader size="md" />
  }

  return (
    <>
      <LXTable
        items={items}
        columns={columns}
        onRowClick={operation => navigate(`/videosPromotions/${operation.id}`)}
      />
    </>
  )
}
